import { supabase, supabaseUrl } from "@/lib/supabase";

/**
 * Updates or creates a report for a check-in.
 * This should be called when:
 * 1. A check-in is activated
 * 2. A check-in's schedule is changed
 * 
 * @param checkInId The ID of the check-in
 * @returns An object indicating success or failure
 */
export const createOrUpdateReportForCheckIn = async (checkInId: string) => {
  try {
    console.log(`[REPORT-UTILS] Starting createOrUpdateReportForCheckIn for check-in ${checkInId}`);
    
    // First check if check-in has participants
    const hasAnyParticipants = await hasParticipants(checkInId);
    console.log(`[REPORT-UTILS] Has participants check result: ${hasAnyParticipants}`);
    
    if (!hasAnyParticipants) {
      console.log(`[REPORT-UTILS] No participants found for check-in ${checkInId}, aborting report creation`);
      return {
        success: false,
        error: 'Cannot create report for a check-in with no participants'
      };
    }
    
    // Get current session access token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error(`[REPORT-UTILS] Session error:`, sessionError);
    }
    
    if (!session) {
      console.error(`[REPORT-UTILS] No active session found`);
      throw new Error('No active session');
    }
    
    console.log(`[REPORT-UTILS] Got valid session, preparing to call edge function`);
    
    // Call the edge function to create/update the report
    try {
      const url = `${supabaseUrl}/functions/v1/report-cycle-management`;
      console.log(`[REPORT-UTILS] Calling edge function at: ${url}`);
      
      const requestBody = {
        checkInId,
        action: 'create_or_update_report'
      };
      console.log(`[REPORT-UTILS] Request body:`, requestBody);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log(`[REPORT-UTILS] Edge function response status:`, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[REPORT-UTILS] Error response from edge function: ${response.status} ${errorText}`);
        return {
          success: false,
          error: `Failed to create/update report: ${response.status} ${errorText}`
        };
      }
      
      const result = await response.json();
      console.log(`[REPORT-UTILS] Edge function success response:`, result);
      
      return {
        success: true,
        data: { reportId: result.reportId }
      };
    } catch (fetchError) {
      console.error(`[REPORT-UTILS] Error during edge function call:`, fetchError);
      throw fetchError;  // Re-throw to be caught by outer try-catch
    }
  } catch (error) {
    console.error('[REPORT-UTILS] Error in createOrUpdateReportForCheckIn:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

/**
 * Helper to call the calculate-reminders edge function
 * @param checkInId The ID of the check-in
 * @param employeeId Optional specific employee ID
 * @param isActivation Whether this is for check-in activation
 */
export const callCalculateReminders = async (checkInId: string, employeeId?: string, isActivation: boolean = false) => {
  try {
    // First check if check-in has participants (when activating)
    if (isActivation) {
      const hasAnyParticipants = await hasParticipants(checkInId);
      if (!hasAnyParticipants) {
        return {
          success: false,
          error: 'Cannot calculate reminders for a check-in with no participants'
        };
      }
    }
    
    // Get current session access token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }
    
    const requestBody: Record<string, any> = {
      checkInId,
      isActivation
    };
    
    if (employeeId) {
      requestBody.employeeId = employeeId;
    }
    
    const response = await fetch(`${supabaseUrl}/functions/v1/calculate-reminders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error calculating reminders: ${response.status} ${errorText}`);
      return {
        success: false,
        error: `Failed to calculate reminders: ${response.status} ${errorText}`
      };
    }
    
    const result = await response.json();
    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('Error in callCalculateReminders:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

// Check if a check-in has participants before allowing activation
export const hasParticipants = async (checkInId: string): Promise<boolean> => {
  try {
    const { count, error } = await supabase
      .from('check_in_employees')
      .select('*', { count: 'exact', head: true })
      .eq('check_in_id', checkInId);
    
    if (error) {
      console.error('Error checking participants:', error);
      return false;
    }
    
    return count !== null && count > 0;
  } catch (error) {
    console.error('Error checking for participants:', error);
    return false;
  }
};

/**
 * Snapshots the current report_config and active_question_ids from the check-in into the report's report_instance_config.
 * @param reportId The ID of the report to update
 * @param checkInId The ID of the check-in to snapshot from
 * @param retryCount Optional retry count for internal use
 * @returns Object indicating success or failure
 */
export const snapshotReportConfigToReport = async (reportId: string, checkInId: string, retryCount = 0): Promise<{ success: boolean, error?: string }> => {
  console.log(`[REPORT-UTILS] Starting snapshotReportConfigToReport for report ${reportId} from check-in ${checkInId} (Attempt: ${retryCount + 1})`);

  if (retryCount > 0) {
    const delay = 1000 * Math.pow(2, retryCount -1) // Exponential backoff: 1s, 2s, 4s
    console.log(`[REPORT-UTILS] Waiting ${delay / 1000}s before retry...`);
    await new Promise(resolve => setTimeout(resolve, delay));
  } else {
    // Initial short delay
    console.log(`[REPORT-UTILS] Waiting 1 second before initial update attempt...`);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  try {
    // Fetch check-in's report_config and active_question_ids
    const { data: checkIn, error: checkInError } = await supabase
      .from('check_ins')
      .select('report_config, active_question_ids')
      .eq('check_in_id', checkInId)
      .single();

    if (checkInError) {
      console.error('[REPORT-UTILS] Error fetching check-in data:', checkInError);
      throw checkInError;
    }
    if (!checkIn) {
      console.error('[REPORT-UTILS] Check-in not found:', checkInId);
      return { success: false, error: `Check-in ${checkInId} not found.` };
    }

    console.log(`[REPORT-UTILS] Got check-in data:`, {
      hasReportConfig: !!checkIn.report_config,
      reportConfigKeys: checkIn.report_config ? Object.keys(checkIn.report_config) : 'N/A',
      activeQuestionIdsCount: (checkIn.active_question_ids || []).length
    });

    const questionIds = checkIn.active_question_ids || [];
    let questions: any[] = [];
    if (questionIds.length > 0) {
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('question_id, header, prompt_instructions')
        .in('question_id', questionIds);
      if (questionsError) {
        console.error('[REPORT-UTILS] Error fetching questions:', questionsError);
        throw questionsError;
      }
      questions = questionsData || [];
      console.log(`[REPORT-UTILS] Found ${questions.length} questions out of ${questionIds.length} requested`);
    }

    const reportInstanceConfig = {
      ...(checkIn.report_config || {
        customReportComponents: [],
        includeIndividualUpdatesComponent: true,
        aiSummaries: []
      }),
      activeQuestions: questions.map(q => ({
        questionId: q.question_id,
        header: q.header,
        promptInstructions: q.prompt_instructions
      }))
    };
    console.log(`[REPORT-UTILS] Prepared reportInstanceConfig (camelCase for attempts):`, JSON.stringify(reportInstanceConfig).substring(0, 500) + "...");


    let updateSuccessful = false;

    // --- Attempt 1: Standard Supabase Update ---
    console.log(`[REPORT-UTILS] Attempt 1: Using supabase.from('reports').update() with camelCase config.`);
    const { error: updateError } = await supabase
      .from('reports')
      .update({ 
        report_instance_config: reportInstanceConfig, 
        report_instance_questions: questionIds 
      })
      .eq('report_id', reportId);

    if (updateError) {
      console.error('[REPORT-UTILS] Attempt 1 (standard update) FAILED during .update() call:', updateError);
    } else {
      console.log('[REPORT-UTILS] Attempt 1 (standard update) .update() call reported success. Verifying...');
      const { data: verifyData, error: verifyError } = await supabase
        .from('reports')
        .select('report_instance_config')
        .eq('report_id', reportId)
        .single();

      if (verifyError) {
        console.error('[REPORT-UTILS] Attempt 1 (standard update) VERIFICATION FAILED (error on select):', verifyError);
      } else if (verifyData && verifyData.report_instance_config && 
                 verifyData.report_instance_config.aiSummaries && verifyData.report_instance_config.aiSummaries.length > 0) {
        console.log('[REPORT-UTILS] Attempt 1 (standard update) VERIFIED successfully. Config:', JSON.stringify(verifyData.report_instance_config).substring(0, 300) + "...");
        updateSuccessful = true;
      } else {
        console.warn('[REPORT-UTILS] Attempt 1 (standard update) VERIFICATION FAILED (data not saved correctly or empty). Current DB value:', JSON.stringify(verifyData?.report_instance_config));
      }
    }

    // --- Attempt 2: RPC Call (if standard update failed verification) ---
    if (!updateSuccessful) {
      console.log('[REPORT-UTILS] Attempt 2: Using RPC call update_report_config with camelCase config.');
      try {
        // Pass the JS object directly; Supabase client handles JSON stringification for RPC if the function expects json/jsonb
        const { error: rpcError } = await supabase.rpc('update_report_config', {
          p_report_id: reportId,
          p_config: reportInstanceConfig,
        });

        if (rpcError) {
          console.error('[REPORT-UTILS] Attempt 2 (RPC) FAILED during .rpc() call:', rpcError);
          if (rpcError.message?.includes('function update_report_config unknown')) {
            console.error('[REPORT-UTILS] CRITICAL: The RPC function "update_report_config" does not exist in the database.');
          } else if (rpcError.message?.includes('permission denied')) {
            console.error('[REPORT-UTILS] Attempt 2 (RPC) Error: Possible RLS/permission issue with the RPC function or underlying table.');
          }
        } else {
          console.log('[REPORT-UTILS] Attempt 2 (RPC) .rpc() call reported success. Verifying...');
          const { data: verifyRpcData, error: verifyRpcError } = await supabase
            .from('reports')
            .select('report_instance_config')
            .eq('report_id', reportId)
            .single();

          if (verifyRpcError) {
            console.error('[REPORT-UTILS] Attempt 2 (RPC) VERIFICATION FAILED (error on select):', verifyRpcError);
          } else if (verifyRpcData && verifyRpcData.report_instance_config && 
                     verifyRpcData.report_instance_config.aiSummaries && verifyRpcData.report_instance_config.aiSummaries.length > 0) {
            console.log('[REPORT-UTILS] Attempt 2 (RPC) VERIFIED successfully. Config:', JSON.stringify(verifyRpcData.report_instance_config).substring(0, 300) + "...");
            updateSuccessful = true;
          } else {
            console.warn('[REPORT-UTILS] Attempt 2 (RPC) VERIFICATION FAILED (data not saved correctly or empty). Current DB value:', JSON.stringify(verifyRpcData?.report_instance_config));
          }
        }
      } catch (e: any) {
        console.error('[REPORT-UTILS] Attempt 2 (RPC) Exception during call or verification:', e);
      }
    }

    // --- Outcome & Retry Logic ---
    if (updateSuccessful) {
      console.log(`[REPORT-UTILS] Report ${reportId} updated successfully.`);
      return { success: true };
    } else {
      console.error('[REPORT-UTILS] All update attempts failed.');
      return { success: false, error: 'All update attempts failed' };
    }
  } catch (error) {
    console.error('[REPORT-UTILS] Error in snapshotReportConfigToReport:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};
